import { uniqBy } from "lodash-es";

import {
  DecisionHistoryFiltersV2,
  NODE_EXECUTIONS_POLLING_INTERVAL,
  NodeExecutionStatusFilter,
  useGetNodeExecutions,
  useHistoryDecisionsV2,
} from "src/api/decisionHistoryV2/decisionHistoryQueries";
import { ErrorBaseInfo, ErroredRow } from "src/api/types";
import { StatusFilterKey } from "src/dataTable/TableUtils";
import { ResultDataAndAuxRowV2 } from "src/dataTable/types";
import { errorMessage } from "src/utils/stringUtils";
import { assertUnreachable } from "src/utils/typeUtils";

export type CustomStatusFilterKey = Extract<
  StatusFilterKey,
  "success" | "failure" | "ignored" | "all"
>;

const parseDecisionError = (error: string, nodeId: string): ErrorBaseInfo => {
  try {
    return JSON.parse(error) as ErrorBaseInfo;
  } catch {
    return {
      msg: error,
      node_id: nodeId,
      exc_type: "unknown_error",
      loc_type: "node",
      loc: nodeId,
    };
  }
};

export const transformNodeResultsToResultDataRows = (
  nodeResults: ReturnType<typeof useGetNodeExecutions>["data"],
  rowIdToIndex: Record<string, number>,
) => {
  const results: ResultDataAndAuxRowV2[] = [];
  const errors = new Map<number, ErroredRow>();

  const deduplicatedResults = uniqBy(
    nodeResults?.pages.flatMap((page) => page.results) ?? [],
    "decision_metadata.id",
  );

  deduplicatedResults.forEach((result, index) => {
    const effectiveIndex = result.decision_metadata.source_entry_id
      ? (rowIdToIndex[result.decision_metadata.source_entry_id] ?? index)
      : index;
    if (result.decision_metadata.status_code === "200") {
      results.push({
        index: effectiveIndex,
        type: "success",
        data: result.data,
        auxData: {},
        decisionId: result.decision_metadata.id,
        nodeExecutionMetadata: result.execution_metadata,
      });
      return;
    }

    if (result.decision_metadata.error) {
      const parsedError = parseDecisionError(
        result.decision_metadata.error,
        result.node_id,
      );
      const isIgnored = result.decision_metadata.status_code === "499";
      const isErroredNode = result.node_id === parsedError.node_id;
      results.push({
        index: effectiveIndex,
        type: isErroredNode ? (isIgnored ? "ignored" : "failure") : "success",
        auxData: {},
        data: result.data,
        errorMessage: errorMessage(
          parsedError.msg ?? result.decision_metadata.error,
        ),
        errorOriginNodeId: parsedError.node_id ?? result.node_id,
        nodeExecutionMetadata: result.execution_metadata,
        decisionId: result.decision_metadata.id,
      });
      errors.set(effectiveIndex, {
        ...parsedError,
        index: effectiveIndex,
        data: result.data,
        aux_data: {},
        error_hash: "",
      });
      return;
    }

    results.push({
      index: effectiveIndex,
      type: "ignored",
      data: result.data,
      auxData: {},
      decisionId: result.decision_metadata.id,
      nodeExecutionMetadata: result.execution_metadata,
    });
  });

  return { dataToDisplay: results, erroredRows: errors };
};

export const transformDecisionsToResultDataRows = (
  decisions: ReturnType<typeof useHistoryDecisionsV2>["data"],
  rowIdToIndex: Record<string, number>,
) => {
  const results: ResultDataAndAuxRowV2[] = [];
  const errors = new Map<number, ErroredRow>();

  const deduplicatedDecisions = uniqBy(
    decisions?.pages.flatMap((page) => page.data) ?? [],
    "id",
  );

  deduplicatedDecisions.forEach((decision, index) => {
    const decisionId = decision.id;
    const effectiveIndex = decision.source_entry_id
      ? (rowIdToIndex[decision.source_entry_id] ?? index)
      : index;
    if (decision.is_error && decision.error) {
      if (decision.error.exc_type === "row_ignored") {
        results.push({
          index: effectiveIndex,
          type: "ignored",
          data: decision.error.data ?? {},
          auxData: {},
          decisionId,
          errorMessage: errorMessage(decision.error.msg),
          errorOriginNodeId: decision.error.node_id,
          nodeExecutionMetadata: decision.node_results.node_execution_metadata,
        });
      } else if (decision.error.exc_type === "output_mismatch_error") {
        results.push({
          index: effectiveIndex,
          type: "success_mismatch",
          data: decision.error.data ?? {},
          expectedOutputKeysMismatch: decision.error.mismatched_keys,
          expectedOutputData: decision.error.expected_output_data,
          auxData: {},
          decisionId,
          nodeExecutionMetadata: decision.node_results.node_execution_metadata,
        });
      } else {
        results.push({
          index: effectiveIndex,
          errorMessage: errorMessage(decision.error.msg),
          type: "failure",
          data: decision.error.data ?? {},
          auxData: {},
          errorOriginNodeId: decision.error.node_id,
          decisionId,
        });
        errors.set(effectiveIndex, {
          data: {},
          aux_data: {},
          ...decision.error,
          index: effectiveIndex,
          error_hash: "",
        });
      }
      return;
    }

    if (!decision.is_error) {
      const isPending = decision.status_code === "202";
      if (isPending) {
        results.push({
          index: effectiveIndex,
          type: "pending",
          data: decision.response.data,
          nodeExecutionMetadata: decision.node_results.node_execution_metadata,
          decisionId,
        });
      } else {
        results.push({
          index: effectiveIndex,
          type: "success",
          data: decision.response.data,
          nodeExecutionMetadata: decision.node_results.node_execution_metadata,
          auxData: {},
          decisionId,
        });
      }
    }
  });

  return { dataToDisplay: results, erroredRows: errors };
};

export const computeDecisionsRefetchInterval = ({
  data,
  totalRowsCount,
}: {
  data:
    | {
        pages: {
          data: unknown[];
        }[];
      }
    | undefined;
  totalRowsCount: number;
}): { shouldPoll: boolean; interval: number | false } => {
  if (!data) {
    return { shouldPoll: true, interval: NODE_EXECUTIONS_POLLING_INTERVAL };
  }

  const loadedPagesCount = data.pages.length;
  const computedExpectedResultsCount = loadedPagesCount * 100; // based on the page size
  // we cap the expected results count at the total rows count
  const effectiveExpectedResultsCount = Math.min(
    computedExpectedResultsCount,
    totalRowsCount,
  );
  const resultsCount = data.pages.reduce(
    (acc, page) => acc + page.data.length,
    0,
  );

  // if two pages are loaded we only expect 200 rows (based on the page size)
  // but if less rows are loaded than expected then we keep polling
  if (resultsCount >= effectiveExpectedResultsCount) {
    return { shouldPoll: false, interval: false };
  }

  return { shouldPoll: true, interval: NODE_EXECUTIONS_POLLING_INTERVAL };
};

export const toDecisionStatusFilter = (
  statusFilter: CustomStatusFilterKey,
): DecisionHistoryFiltersV2["fields"]["status"] => {
  switch (statusFilter) {
    case "success":
      return "success";
    // Unable to filter on ignored as failure already includes ignored for decision history
    // disabled ignored filter for output node, added here for completeness
    case "failure":
    case "ignored":
      return "failure";
    case "all":
      return undefined;
    default:
      return assertUnreachable(statusFilter);
  }
};

export const toNodeExecutionStatusFilter = (
  statusFilter: CustomStatusFilterKey,
): NodeExecutionStatusFilter[] | undefined => {
  switch (statusFilter) {
    case "success":
      return ["successful"] as const;
    case "failure":
      return ["failed"] as const;
    case "ignored":
      return ["ignored"] as const;
    case "all":
      return undefined;
    default:
      return assertUnreachable(statusFilter);
  }
};
